/* THE BUS — render scheduling and cross-layer notifications.

   Imports nothing (see the note in store.js). This is what breaks the cycle the
   old single-file version had for free: the scene layer used to call renderRails()
   directly from ~15 sites, which as modules would mean scene → ui → scene. Now the
   scene emits and ui/rails.js listens.

   Events in use:
     'scene:changed'   an object was added/removed/renamed — rebuild the rails
     'sel:changed'     selection moved — rebuild the inspector
     'params:changed'  the selected object's numbers moved — refresh values only
     'solve:changed'   calibration re-solved
     'view:changed'    orbit / flat / site
     'history:changed' undo/redo stacks moved — update button disabled states
     'anim:changed'    a track or keyframe moved — rebuild the timeline
     'motion:changed'  the Advanced licence was granted or expired
     'auth:changed'    signed in or out
     'entitlements:changed' credits or a purchase landed — relock/unlock UI
*/

/* ── render scheduling ───────────────────────────────────────────────────────
   The app draws on demand, not every frame. Anything that changes the picture
   calls requestRender(); the loop in scene/renderer.js consumes the flag. */

let _dirty = true;
let _held = 0;

/** Mark the frame dirty. Cheap and idempotent — call it freely. */
export function requestRender() { _dirty = true; }

/** Consume the dirty flag. Returns true at most once per requestRender().
 *  Always false while rendering is held (see holdRendering), and the flag is
 *  kept, so whatever was asked for in the meantime is drawn once it lets go. */
export function consumeRender() {
  if (_held > 0 || !_dirty) return false;
  _dirty = false;
  return true;
}

/* HOLDING THE PREVIEW while an export owns the renderer.

   There is one WebGLRenderer and one canvas, shared by the live preview and
   every exporter. A video export awaits between frames — the encoder, clips
   decoding — and each frame's settleAt asks for a redraw, so the preview's
   loop used to run in the gaps and redraw the view in between export frames.
   In Split that meant resizing the renderer for its two panes at twice the
   pixel ratio, turning the lamp and camera markers back on and refitting the
   shadows: the next export frames came out at four times the pixels (over
   three times slower) with the lamp markers baked into the video. The fix is
   not to undo each thing the preview does, but for the preview not to run.

   Returns the release. Calling it more than once is harmless, so a caller
   can release in a finally without tracking whether it already has. */
export function holdRendering() {
  _held++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    _held = Math.max(0, _held - 1);
    _dirty = true;          // redraw the view the export left behind
  };
}

/* ── pub/sub ────────────────────────────────────────────────────────────── */

const listeners = new Map();

/** Subscribe. Returns an unsubscribe function. */
export function on(event, fn) {
  let L = listeners.get(event);
  if (!L) { L = new Set(); listeners.set(event, L); }
  L.add(fn);
  return () => L.delete(fn);
}

export function off(event, fn) {
  const L = listeners.get(event);
  if (L) L.delete(fn);
}

/**
 * Fire an event. A throwing listener is reported but never stops the others —
 * a broken inspector must not take the render loop down with it.
 */
export function emit(event, payload) {
  const L = listeners.get(event);
  if (!L) return;
  for (const fn of Array.from(L)) {
    try { fn(payload); }
    catch (err) { console.error('[bus] listener failed for "' + event + '"', err); }
  }
}

/* ── coalescing ─────────────────────────────────────────────────────────────
   Dragging a gizmo fires params:changed on every pointermove. Listeners that
   rebuild DOM should not run at pointer rate, so they subscribe through this
   and get at most one call per animation frame. */

const pending = new Map();

export function emitNextFrame(event, payload) {
  if (pending.has(event)) { pending.set(event, payload); return; }
  pending.set(event, payload);
  requestAnimationFrame(() => {
    const p = pending.get(event);
    pending.delete(event);
    emit(event, p);
  });
}
